import { useState, useRef } from 'react';
import { Trans } from '@lingui/macro';
import styled from '@emotion/styled';
import { Text } from 'components/text/Text';
import { NumberInput, NumberInputProps } from 'components/inputs/NumberInput';

type NumberProps = {
  from?: number | '';
  to?: number | '';
  onChange: (from?: number | '', to?: number | '') => void;
  onBlur: () => void;
  onKeyDown?: NumberInputProps['onKeyDown'];
  fromPlaceholder?: string;
  toPlaceholder?: string;
  'data-testid'?: string;
};

export const Number = ({
  from,
  to,
  onChange,
  onBlur,
  onKeyDown,
  toPlaceholder = 'e.g. 9',
  fromPlaceholder = 'e.g. 1',
  'data-testid': dataTestId,
}: NumberProps) => {
  const [rangeError, setRangeError] = useState(false);
  const pendingFrom = useRef(from);
  const pendingTo = useRef(to);

  const checkRangeError = (nextFrom: number | '' | undefined, nextTo: number | '' | undefined) => {
    const parsedFrom = nextFrom !== '' && nextFrom != null ? parseFloat(String(nextFrom)) : null;
    const parsedTo = nextTo !== '' && nextTo != null ? parseFloat(String(nextTo)) : null;
    const isInvalid = parsedFrom != null && parsedTo != null && parsedFrom > parsedTo;
    setRangeError(isInvalid);
    return !isInvalid;
  };

  return (
    <Container>
      <FromTo>
        <NumberInput
          placeholder={fromPlaceholder}
          variant="outlined"
          value={from == null ? '' : String(from)}
          allowSignedValues
          autoFocus
          onChange={e => {
            const val = (e.target.value ?? '') as unknown as number;
            pendingFrom.current = val;
            onChange(val, to);
          }}
          onBlur={() => {
            if (checkRangeError(pendingFrom.current, pendingTo.current)) onBlur();
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              if (checkRangeError(pendingFrom.current, pendingTo.current)) {
                onKeyDown?.(e);
              }
            }
          }}
          data-testid={`${dataTestId}_from`}
        />
        <span data-testid={`${dataTestId}_divider_to`}>
          <Trans>to</Trans>
        </span>
        <NumberInput
          placeholder={toPlaceholder}
          variant="outlined"
          value={to == null ? '' : String(to)}
          allowSignedValues
          onChange={e => {
            const val = (e.target.value ?? '') as unknown as number;
            pendingTo.current = val;
            onChange(from, val);
          }}
          onBlur={() => {
            if (checkRangeError(pendingFrom.current, pendingTo.current)) onBlur();
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              if (checkRangeError(pendingFrom.current, pendingTo.current)) {
                onKeyDown?.(e);
              }
            }
          }}
          data-testid={`${dataTestId}_to`}
        />
      </FromTo>
      {rangeError && (
        <Text color="error" variant="bodySm">
          <Trans>Start value must be lower than end value</Trans>
        </Text>
      )}
    </Container>
  );
};

const FromTo = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
  > div {
    min-width: 186px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  > span {
    margin-left: ${p => p.theme.s.xxxs};
    margin-right: ${p => p.theme.s.xxxs};
  }
`;
